"use client";

import { useState, useEffect } from "react";
import CommonSchema from "@/components/common/CommonSchema";
import { motion, AnimatePresence } from "motion/react";
import {
  Sparkles,
  ShieldCheck,
  CheckCircle2,
  Compass,
  Phone,
  Mail,
  ArrowRight,
  Star,
  Check,
  X,
  BedDouble,
  Briefcase,
  Users,
  Heart,
  ChevronDown,
} from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { FadeInSection } from "@/components/common/FadeInSection";
import { Banner } from "@/components/common/Banner";
import { useAppSelector } from "@/store/hooks";
import {
  cleanHeaderTags,
  createLookupObject,
  isValidImgSrc,
} from "@/_helper/helper";
import { ISettings } from "@/slices/homeSlice";

interface BookDirectClientProps {
  cmsData?: any[];
  initialBanner?: any;
}

export default function BookDirectClient({
  cmsData,
  initialBanner,
}: BookDirectClientProps) {
  const BannerList = useAppSelector((state) => state.home.BannerList);
  // const RoomList = useAppSelector((state) => state.room.RoomList);
  // const SettingList = useAppSelector((state) => state.home.SettingList);

  // const Settings: Record<string, ISettings> = SettingList?.reduce(
  //   (acc, item) => {
  //     acc[item?.SettingKey] = item;
  //     return acc;
  //   },
  //   {} as Record<string, ISettings>,
  // );

  // const SwiftbookLink =
  //   Settings?.["SwiftbookLink"]?.SettingValue ||
  //   "https://sarasiruhamresort.com/booking";
  // const PhoneNo = Settings?.["PhoneNo"]?.SettingValue || "+91 91161 39401";
  // const EmailID =
  //   Settings?.["EmailID"]?.SettingValue || "reservations@sarasiruhamresort.com";

  const [lookUpCMSData, setLookUpCMSData] = useState<Record<string, any>>({});
  const [isTermsOpen, setIsTermsOpen] = useState(false);

  useEffect(() => {
    if (cmsData && cmsData.length > 0) {
      setLookUpCMSData(createLookupObject(cmsData, "Name"));
    }
  }, [cmsData]);

  const banner: any =
    initialBanner ||
    BannerList?.find(
      (b: any) => b?.BannerType?.toLowerCase() === "sarasiruham-direct-book",
    ) ||
    {};

  // Direct Booking Privileges
  const directBenefits = [
    {
      title: "A Room for Every Moment",
      points: ["Luxury Pool Suite", "Lakeview Suite", "Garden View Suite"],
      icon: BedDouble,
    },
    {
      title: "Corporate Experiences",
      points: [
        "Make Stronger Teams",
        "Exchange Fresh Ideas",
        "Build lasting memories",
      ],
      icon: Briefcase,
    },
    {
      title: "Social & Family Get Together",
      points: [
        "Elegant Spaces for Every Gathering",
        "Memorable Moments with Loved Ones",
        "Perfect Setting for Family Celebrations",
      ],
      icon: Users,
    },
    {
      title: "Anniversary Celebration",
      points: [
        "Beautifully decorated rooms for an anniversary celebration.",
        "Enjoy a romantic dinner under the soft glow of candlelight.",
        "Celebrate your special day in a private, intimate setting.",
      ],
      icon: Heart,
    },
    {
      title: "Activities to experience (Additional Charges)",
      points: [
        "Horse Riding: Enjoy a leisurely ride amidst nature.",
        "E-Cycling: Explore the surroundings at your own pace.",
        "Live Bilona: Experience the traditional art of bilona.",
      ],
      icon: Compass,
    },
  ];

  // Comparison Matrix
  const comparisonItems = [
    {
      feature: "Lowest Price Guarantee",
      direct: "Guaranteed + 10% Extra Savings if lower rate found",
      ota: "Standard rates with no price match guarantee",
      isDirectBetter: true,
    },
    {
      feature: "Dining & Spa Discounts",
      direct: "15% off dining and wellness therapies",
      ota: "Not available",
      isDirectBetter: true,
    },
    {
      feature: "Room & View Preferences",
      direct: "Priority suite allocation & custom view requests",
      ota: "Random automated room assignment",
      isDirectBetter: true,
    },
    {
      feature: "Early Check-In / Late Check-Out",
      direct: "Priority complimentary extension (subject to availability)",
      ota: "Subject to strict standard hours or paid fees",
      isDirectBetter: true,
    },
    {
      feature: "Booking Transparency",
      direct: "No hidden booking or administration fees",
      ota: "Convenience fees or portal service surcharges",
      isDirectBetter: true,
    },
    {
      feature: "Dedicated Concierge Support",
      direct: "Direct access to resort host for customized itineraries",
      ota: "Generic chatbot or third-party call center",
      isDirectBetter: true,
    },
    {
      feature: "Special Celebration Setup",
      direct: "Personalized anniversary, honeymoon & birthday arrangements",
      ota: "Requires manual follow-up without confirmation",
      isDirectBetter: true,
    },
  ];

  // Terms and conditions accordion items
  const termsList = [
    {
      title: "1. Scope of the Best Rate Guarantee",
      content:
        "We assure you the best available rate when you book accommodation directly on our official website (www.sarasiruhamresort.com). In the event you find a lower publicly available rate on another authorized online public platform within 24 hours of completing your reservation with us, we will happily match that lower rate and apply an additional 10% savings on your room tariff, subject to these terms.",
    },
    {
      title: "2. Qualifying Criteria for Competing Rates",
      content:
        "The competing rate must be bookable in real-time on an authorized online booking website for the identical hotel, room or suite category (including bed type, view, and amenities), exact same check-in and check-out dates, number of guests, currency, and identical booking rules (including deposit, payment, and cancellation policies).",
    },
    {
      title: "3. Non-Eligible Rates & Exclusions",
      content:
        "The Best Rate Guarantee does not apply to package rates (e.g., bundled hotel stays with flights, ground transport, or tour tickets), opaque or auction booking websites where the resort name is not disclosed until booking, unpublished or negotiated corporate contracts, group bookings, private membership club rates, closed-user group rates, direct email or mail solicitations, or cashback coupon schemes not open to the general public.",
    },
    {
      title: "4. Currency Exchange Calculations",
      content:
        "If the competing rate is quoted in a currency different from the Indian Rupee (INR) displayed on our official website, the rate will be converted using current prevailing interbank exchange rates. Minor discrepancies solely attributable to currency exchange fluctuations will not be eligible for claims under this guarantee.",
    },
    {
      title: "5. How to Avail the Guarantee (No Form Required)",
      content:
        "To verify a competing rate, simply contact our reservations team via phone or email within 24 hours of booking directly on our website, and at least 48 hours prior to your scheduled check-in time. Please provide your booking confirmation number, the web link (URL), and a screenshot showing the lower competing rate with matching dates and room category. Our dedicated team will verify and adjust your reservation within 2 business days.",
    },
    {
      title: "6. Modification & General Provisions",
      content:
        "Sarasiruham Resort reserves the right to determine the validity of any claim in accordance with these Terms and Conditions. The guarantee does not apply to modified or cancelled reservations, or during technical outages beyond reasonable control. In all matters regarding this policy, the decision of the resort management remains final.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#fdfcfa]">
      <CommonSchema />
      {/* Banner */}
      <Banner
        title={banner?.BannerTitle || ""}
        // highlightedTitle={banner?.highlightedTitle || "& Best Rate Promise"}
        description={banner?.Description || ""}
        bgImage={banner?.BannerImage || "/imports/HeroVideoImage.jpg"}
        backLinkText="Home"
        backLinkUrl="/"
        isBack={true}
      />

      {/* Intro & Overview Bar */}
      {lookUpCMSData?.["Book Direct - Section 1 : Statistics"] && (
        <section className="py-12 bg-white border-b border-[#2a2520]/10 relative z-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div
              className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center divide-y md:divide-y-0 md:divide-x divide-[#2a2520]/10"
              dangerouslySetInnerHTML={{
                __html: cleanHeaderTags(
                  lookUpCMSData?.["Book Direct - Section 1 : Statistics"]
                    ?.Template,
                ),
              }}
            />
            {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center divide-y md:divide-y-0 md:divide-x divide-[#2a2520]/10">
            <div className="pt-4 md:pt-0">
              <span
                className="text-3xl md:text-4xl text-[#d4af37] block font-semibold"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                100%
              </span>
              <p
                className="text-xs md:text-sm text-[#2a2520]/75 uppercase tracking-wider mt-1"
                style={{ fontFamily: "Jost, sans-serif" }}
              >
                Best Rate Guarantee
              </p>
            </div>
            <div className="pt-4 md:pt-0">
              <span
                className="text-3xl md:text-4xl text-[#d4af37] block font-semibold"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                15% OFF
              </span>
              <p
                className="text-xs md:text-sm text-[#2a2520]/75 uppercase tracking-wider mt-1"
                style={{ fontFamily: "Jost, sans-serif" }}
              >
                Dining & Spa Perks
              </p>
            </div>
            <div className="pt-4 md:pt-0">
              <span
                className="text-3xl md:text-4xl text-[#d4af37] block font-semibold"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                0% Fees
              </span>
              <p
                className="text-xs md:text-sm text-[#2a2520]/75 uppercase tracking-wider mt-1"
                style={{ fontFamily: "Jost, sans-serif" }}
              >
                Zero Hidden Charges
              </p>
            </div>
            <div className="pt-4 md:pt-0">
              <span
                className="text-3xl md:text-4xl text-[#d4af37] block font-semibold"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                Direct
              </span>
              <p
                className="text-xs md:text-sm text-[#2a2520]/75 uppercase tracking-wider mt-1"
                style={{ fontFamily: "Jost, sans-serif" }}
              >
                Concierge Assistance
              </p>
            </div>
          </div> */}
          </div>
        </section>
      )}

      {/* Section 1: Why Book With Us Online? (Core Privileges Grid) */}
      {lookUpCMSData?.["Book Direct - Section 2 : Why Book Directly"] && (
        <section className="py-16 md:py-24 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#d4af37]/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#5a6f3e]/10 rounded-full blur-3xl pointer-events-none" />

          {/* CMS Template Injection (if available) */}
          <FadeInSection>
            <div
              dangerouslySetInnerHTML={{
                __html: cleanHeaderTags(
                  lookUpCMSData?.["Book Direct - Section 2 : Why Book Directly"]
                    ?.Template,
                ),
              }}
              className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10"
            />
          </FadeInSection>
          {/* <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <FadeInSection>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#d4af37]/15 text-[#2a2520] text-xs font-semibold uppercase tracking-widest mb-4">
                <Sparkles className="w-3.5 h-3.5 text-[#d4af37]" />
                Exclusive Direct Privileges
              </div>
              <h2
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-[#2a2520] mb-4 leading-tight"
                style={{
                  fontFamily: "Cormorant Garamond, serif",
                  fontWeight: 400,
                }}
              >
                Why Book With Us{" "}
                <span className="italic text-[#d4af37] font-semibold">
                  Directly?
                </span>
              </h2>
              <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-[#d4af37] to-transparent mx-auto mb-5" />
              <p
                className="text-[#2a2520]/80 text-base md:text-lg leading-relaxed"
                style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
              >
                Planning your stay at Sarasiruham Resort? Book directly through
                our website and enjoy the confidence of booking with us, along
                with exclusive benefits and our best available rates. From a
                seamless booking experience to greater value for your stay, we
                make it easy to plan your Udaipur escape. Choose a direct
                booking and experience the comfort, convenience, and
                personalised hospitality of Sarasiruham Resort from the moment
                you reserve your stay.
              </p>
            </div>
          </FadeInSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {directBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <FadeInSection key={index} delay={index * 0.08}>
                  <motion.div
                    whileHover={{ y: -8 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="bg-white rounded-3xl p-6 sm:p-7 border border-[#2a2520]/10 hover:border-[#d4af37]/50 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col justify-between h-full group"
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-5">
                        <div className="w-12 h-12 min-w-12 rounded-2xl bg-[#fdfcfa] border border-[#d4af37]/30 flex items-center justify-center text-[#d4af37] group-hover:bg-[#d4af37] group-hover:text-[#2a2520] transition-colors duration-300 shadow-sm">
                          <Icon className="w-6 h-6" />
                        </div>
                        <h3
                          className="text-xl sm:text-2xl text-[#2a2520] mb-1 font-semibold group-hover:text-[#d4af37] transition-colors"
                          style={{ fontFamily: "Cormorant Garamond, serif" }}
                        >
                          {benefit.title}
                        </h3>
                      </div>
                      {Array.isArray(benefit.points) &&
                      benefit.points.length > 0 ? (
                        <ul
                          className="space-y-2 text-sm text-[#2a2520]/80 font-light"
                          style={{ fontFamily: "Jost, sans-serif" }}
                        >
                          {benefit.points.map((pt: string, pIdx: number) => (
                            <li
                              key={pIdx}
                              className="flex items-start gap-2.5 leading-relaxed"
                            >
                              <span className="w-2 h-2 min-w-2 rounded-full border border-[#d4af37] flex-shrink-0 mt-1.5" />
                              <span>{pt}</span>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  </motion.div>
                </FadeInSection>
              );
            })}
          </div>
        </div> */}
        </section>
      )}

      {/* Section 2: Sarasiruham Best Rate Promise Showcase (FORM REMOVED AS REQUESTED) */}
      {lookUpCMSData?.["Book Direct - Section 3 : Luxury Escape"] && (
        <section className="py-16 md:py-20 bg-[#2a2520] text-white relative overflow-hidden">
          {/* Decorative Background Accents */}
          <div className="absolute inset-0 opacity-15">
            <Image
              src={
                isValidImgSrc(
                  lookUpCMSData?.["Book Direct - Section 3 : Luxury Escape"]
                    ?.CMSImage,
                )
                  ? lookUpCMSData?.["Book Direct - Section 3 : Luxury Escape"]
                      ?.CMSImage
                  : ""
              }
              alt={
                lookUpCMSData?.["Book Direct - Section 3 : Luxury Escape"]
                  ?.CMSImageAlterTag || "Banner Image"
              }
              fill
              sizes="100vw"
              className="object-cover"
            />
          </div>
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-[#d4af37]/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-[#5a6f3e]/20 rounded-full blur-3xl pointer-events-none" />

          <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
            <FadeInSection>
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md rounded-3xl p-8 sm:p-12 md:p-14 border border-white/15 shadow-2xl">
                <div className="grid grid-cols-1 gap-8 lg:gap-12 items-center">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: cleanHeaderTags(
                        lookUpCMSData?.[
                          "Book Direct - Section 3 : Luxury Escape"
                        ]?.Template,
                      ),
                    }}
                  />
                  {/* <div>
                  <h2
                    className="text-3xl sm:text-4xl md:text-5xl text-white mb-4 leading-tight font-normal"
                    style={{ fontFamily: "Cormorant Garamond, serif" }}
                  >
                    Sarasiruham Resort -{" "}
                    <span className="italic text-[#d4af37] font-semibold">
                      Luxury Escape in Udaipur
                    </span>
                  </h2>

                  <p
                    className="text-white/90 text-base sm:text-lg font-light leading-relaxed mb-6"
                    style={{ fontFamily: "Jost, sans-serif" }}
                  >
                    Discover refined luxury amidst the serene beauty of Udaipur.
                    Sarasiruham Resort blends elegant stays, private pool
                    suites, lakeside tranquillity and warm Rajasthani
                    hospitality for an unforgettable getaway. Recognised as a
                    Tripadvisor Travelers’ Choice Best of the Best 2026, it is
                    an exceptional choice for those seeking comfort, privacy and
                    a truly memorable luxury retreat.
                  </p>
                  <div className="flex flex-wrap items-center gap-4">
                    <Link
                      href={SwiftbookLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <motion.button
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        className="bg-[#d4af37] text-[#2a2520] hover:bg-[#c9a961] px-7 py-3.5 rounded-full font-semibold text-sm flex items-center gap-2.5 transition-all shadow-lg cursor-pointer"
                        style={{ fontFamily: "Jost, sans-serif" }}
                      >
                        <span>Book Directly Now</span>
                        <ArrowRight className="w-4 h-4" />
                      </motion.button>
                    </Link>

                    <a
                      href={`tel:${PhoneNo.replace(/[^0-9+]/g, "")}`}
                      className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/30 text-white hover:bg-white/10 hover:border-white/60 transition-all text-sm font-medium"
                      style={{ fontFamily: "Jost, sans-serif" }}
                    >
                      <Phone className="w-4 h-4 text-[#d4af37]" />
                      <span>Call Concierge</span>
                    </a>

                    <a
                      href={`mailto:${EmailID}?subject=Direct%20Booking%20Inquiry%20-%20Best%20Rate%20Promise`}
                      className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/30 text-white hover:bg-white/10 hover:border-white/60 transition-all text-sm font-medium"
                      style={{ fontFamily: "Jost, sans-serif" }}
                    >
                      <Mail className="w-4 h-4 text-[#d4af37]" />
                      <span>Email Team</span>
                    </a>
                  </div>
                </div> */}
                </div>
              </div>
            </FadeInSection>
          </div>
        </section>
      )}

      {/* Section 4: Terms & Conditions Accordion */}
      {lookUpCMSData?.["Book Direct - Section 4 : Terms And Conditions"] && (
        <section className="py-10 md:py-15">
          <div className="max-w-7xl mx-auto px-6">
            <div className="border border-[#2a2520]/15 rounded-3xl bg-white shadow-xs overflow-hidden transition-all duration-300">
              {/* Clickable Header / Accordion Trigger */}
              <button
                type="button"
                onClick={() => setIsTermsOpen((prev) => !prev)}
                className="w-full p-2 sm:p-4 md:p-6 flex items-center justify-between text-left hover:bg-black/[0.02] transition-colors cursor-pointer group"
                aria-expanded={isTermsOpen}
              >
                <h2
                  className="text-3xl sm:text-4xl md:text-5xl leading-tight text-[#2a2520] group-hover:text-[#d4af37] transition-colors"
                  style={{
                    fontFamily: "Cormorant Garamond, serif",
                    fontWeight: 400,
                  }}
                >
                  Terms &amp; Conditions
                </h2>

                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-[#2a2520]/15 flex items-center justify-center flex-shrink-0 ml-4 group-hover:border-[#d4af37] group-hover:text-[#d4af37] transition-all bg-[#fdfcfa]">
                  <ChevronDown
                    className={`w-5 h-5 sm:w-6 sm:h-6 transition-transform duration-300 ${
                      isTermsOpen
                        ? "rotate-180 text-[#d4af37]"
                        : "text-[#2a2520]"
                    }`}
                  />
                </div>
              </button>

              {/* Collapsible Content */}
              <AnimatePresence initial={false}>
                {isTermsOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{
                      duration: 0.35,
                      ease: [0.04, 0.62, 0.23, 0.98],
                    }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-8 sm:px-8 sm:pb-10 md:px-10 md:pb-12 pt-4 border-t border-[#2a2520]/10 blog-content">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: cleanHeaderTags(
                            lookUpCMSData?.[
                              "Book Direct - Section 4 : Terms And Conditions"
                            ]?.Template,
                          ),
                        }}
                        className="space-y-10"
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </section>
      )}

      {/* Section 5: Direct Booking Suites Preview & Call to Action */}
      {lookUpCMSData?.["Book Direct - Section 5 : CTA"] && (
        <section className="py-20 bg-gradient-to-b from-white to-[#fdfcfa] ">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            {/* <FadeInSection>
            <div className="text-center max-w-3xl mx-auto mb-14">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#d4af37]/15 text-[#2a2520] text-xs font-semibold uppercase tracking-widest mb-4">
                <Sparkles className="w-3.5 h-3.5 text-[#d4af37]" />
                Handcrafted Accommodations
              </div>
              <h2
                className="text-3xl sm:text-4xl md:text-5xl text-[#2a2520] mb-4"
                style={{
                  fontFamily: "Cormorant Garamond, serif",
                  fontWeight: 400,
                }}
              >
                Experience Lakeside Luxury{" "}
                <span className="italic text-[#d4af37] font-semibold">
                  Firsthand
                </span>
              </h2>
              <p
                className="text-[#2a2520]/80 text-base md:text-lg font-light"
                style={{ fontFamily: "Jost, sans-serif" }}
              >
                Choose from our luxury pool villas and panoramic lake view
                suites, and enjoy the guaranteed best rates when reserving
                directly.
              </p>
            </div>
          </FadeInSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {RoomList?.slice(0, 3)?.map((room: any, idx: number) => {
              const roomUrl = `/rooms/${room?.RoomURL || ""}`;
              const imageSrc = isValidImgSrc(room?.Images)
                ? room?.Images
                : idx === 0
                  ? "/imports/image-1.png"
                  : idx === 1
                    ? "/imports/image-2.png"
                    : "/imports/image-3.png";

              return (
                <FadeInSection key={room?.ID || idx} delay={idx * 0.1}>
                  <motion.div
                    whileHover={{ y: -8 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl border border-[#2a2520]/10 flex flex-col h-full group"
                  >
                    <div className="relative h-64 sm:h-72 w-full overflow-hidden">
                      <Image
                        src={imageSrc}
                        alt={room?.RoomTitle || "Sarasiruham Suite"}
                        fill
                        sizes="(max-width: 768px) 100vw, 33vw"
                        className="object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent" />
                      {room?.RoomView && (
                        <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-md text-white text-xs px-3 py-1 rounded-full border border-white/20">
                          {room?.RoomView}
                        </div>
                      )}
                      <div className="absolute bottom-4 left-4 right-4">
                        <span className="text-xs uppercase tracking-wider text-[#d4af37] font-medium block">
                          Official Direct Rate
                        </span>
                        <h3
                          className="text-2xl text-white font-medium"
                          style={{ fontFamily: "Cormorant Garamond, serif" }}
                        >
                          {room?.RoomTitle}
                        </h3>
                      </div>
                    </div>

                    <div className="p-6 flex flex-col flex-1 justify-between">
                      <p
                        className="text-sm text-[#2a2520]/75 line-clamp-2 mb-6 font-light leading-relaxed"
                        style={{ fontFamily: "Jost, sans-serif" }}
                      >
                        {cleanHeaderTags(
                          room?.ShortDescription || room?.Description || "",
                        )}
                      </p>

                      <div className="pt-4 border-t border-[#2a2520]/10 flex items-center gap-3">
                        <Link
                          href={SwiftbookLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1"
                        >
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="w-full bg-[#2a2520] hover:bg-[#d4af37] hover:text-[#2a2520] text-white py-3 px-4 rounded-full text-xs sm:text-sm font-medium transition-colors duration-300 flex items-center justify-center gap-2 cursor-pointer"
                            style={{ fontFamily: "Jost, sans-serif" }}
                          >
                            <span>Book Direct</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                          </motion.button>
                        </Link>
                        <Link
                          href={roomUrl}
                          className="px-4 py-3 rounded-full border border-[#2a2520]/20 hover:border-[#d4af37] text-xs font-medium text-[#2a2520] hover:text-[#d4af37] transition-colors"
                          style={{ fontFamily: "Jost, sans-serif" }}
                        >
                          Details
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                </FadeInSection>
              );
            })}
          </div> */}

            {/* Bottom Luxury Banner */}
            <FadeInSection>
              <div className="rounded-3xl p-8 sm:p-12 text-center bg-gradient-to-r from-[#2a2520] via-[#3a3229] to-[#2a2520] text-white relative overflow-hidden shadow-2xl">
                <div
                  dangerouslySetInnerHTML={{
                    __html: cleanHeaderTags(
                      lookUpCMSData?.["Book Direct - Section 5 : CTA"]
                        ?.Template,
                    ),
                  }}
                  className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10"
                />
                {/* <div className="max-w-2xl mx-auto relative z-10 space-y-4">
                <div className="w-12 h-12 rounded-full bg-[#d4af37]/20 text-[#d4af37] flex items-center justify-center mx-auto mb-2">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <h3
                  className="text-3xl sm:text-4xl text-white font-normal leading-tight"
                  style={{ fontFamily: "Cormorant Garamond, serif" }}
                >
                  Ready for an Unforgettable Stay?
                </h3>
                <p
                  className="text-white/80 text-sm sm:text-base font-light max-w-lg mx-auto leading-relaxed"
                  style={{ fontFamily: "Jost, sans-serif" }}
                >
                  Book directly with us to guarantee your dates, secure best
                  rates, and enjoy our full suite of personal concierge care.
                </p>
                <div className="pt-4 flex flex-wrap justify-center gap-4">
                  <Link
                    href={
                      "https://www.swiftbook.io/inst/#home?propertyId=443NTgKgK2j6hSKdJTD6h4GmAQSvxNjE=&JDRN=Y"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-[#d4af37] text-[#2a2520] hover:bg-[#c9a961] px-8 py-3.5 rounded-full font-semibold text-sm flex items-center gap-2 shadow-lg transition-all cursor-pointer"
                      style={{ fontFamily: "Jost, sans-serif" }}
                    >
                      <span>Reserve Your Suite</span>
                      <ArrowRight className="w-4 h-4" />
                    </motion.button>
                  </Link>
                  <Link
                    href="/contact"
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/30 text-white hover:bg-white/10 text-sm font-medium transition-colors"
                    style={{ fontFamily: "Jost, sans-serif" }}
                  >
                    <span>Contact Us</span>
                  </Link>
                </div>
              </div> */}
              </div>
            </FadeInSection>
          </div>
        </section>
      )}
    </div>
  );
}
